import React from 'react';
import { Copy } from 'react-feather';
import { toast } from 'react-toastify';
import type { BadgeTone } from '../../../ui';
import { Swatch } from './ui-pending';
import {
  CALL_WINDOW_STYLE,
  customerCountry,
  customerLocalTime,
  formatTaskId,
  Task,
} from '../../../Tasks/tasksApi';

/**
 * Shared display bits for tasks — status/priority coloring, the copyable
 * RMT-#### id chip, country flag, and local-time block. Used across the RM
 * Dashboard, task queues, My Learners, and the task detail page
 * (TaskDetailPage.tsx, which replaced this file's original TaskActionModal
 * component — see that file for the task view itself).
 */

/** Call-window dot tone (good / caution / avoid). */
const CALL_WINDOW_TONE: Record<string, BadgeTone> = { GOOD: 'success', CAUTION: 'warning', AVOID: 'danger' };

/**
 * Legacy reactstrap Badge colours. New code uses TaskStatusBadge / PriorityBadge
 * from ./ui-pending, which map the same states to design-system tones.
 */
export const STATUS_COLOR = (s: {
  isInitial: boolean;
  isTerminal: boolean;
  isSuccess: boolean;
}): string =>
  s.isTerminal ? (s.isSuccess ? 'success' : 'danger') : s.isInitial ? 'secondary' : 'warning';

export const PRIORITY_COLOR: Record<Task['priority'], string> = {
  URGENT: 'danger',
  HIGH: 'warning',
  MEDIUM: 'info',
  LOW: 'secondary',
};

/**
 * The task's tracking handle, shown on every screen as RMT-0001. Copies the
 * display id (the API accepts it wherever a task id is expected); the tooltip
 * carries the underlying uuid for deep debugging.
 */
export const TaskIdChip: React.FC<{ taskNumber: number; uuid: string; className?: string }> = ({
  taskNumber,
  uuid,
  className,
}) => {
  const display = formatTaskId(taskNumber);
  return (
    <span
      role="button"
      tabIndex={0}
      title={`${display} (${uuid}) — click to copy`}
      className={className ? `rm-id-chip ${className}` : 'rm-id-chip'}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(display).then(
          () => toast.success(`${display} copied`),
          () => toast.error('Could not copy'),
        );
      }}
      onKeyDown={(e) => e.key === 'Enter' && navigator.clipboard.writeText(display)}
    >
      {display}
      <Copy size={11} aria-hidden />
    </span>
  );
};

/** Country flag from the phone's country code — shown beside every learner name. */
export const CountryFlag: React.FC<{ phone?: string | null; className?: string }> = ({
  phone,
  className,
}) => {
  const c = customerCountry(phone);
  if (!c) return null;
  return (
    <span
      className={`fi fi-${c.iso} rm-flag ${className ?? 'me-2'}`}
      title={c.country}
    />
  );
};

/** Uniform two-row local-time block: dot + time, place beneath. */
export const LocalTimeDisplay: React.FC<{
  local: NonNullable<ReturnType<typeof customerLocalTime>>;
  /** One line ("● 1:10 PM local · ≈ GMT-4") for compact strips. */
  inline?: boolean;
}> = ({ local, inline }) => {
  const win = CALL_WINDOW_STYLE[local.window];
  const dot = <Swatch shape="dot" tone={CALL_WINDOW_TONE[local.window]} />;
  if (inline) {
    return (
      <span title={win.label} className="rm-localtime rm-localtime--inline">
        {dot}
        <strong>{local.time}</strong>
        <span className="ds-text-subtle">
          {'\u00a0'}local · {local.approx ? '≈ ' : ''}
          {local.label}
        </span>
      </span>
    );
  }
  return (
    <span title={win.label} className="rm-localtime">
      <span className="rm-localtime__time">
        {dot}
        {local.time}
      </span>
      <span className="rm-localtime__place">
        {local.approx ? '≈ ' : ''}
        {local.label}
      </span>
    </span>
  );
};
